import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { ResumeCitizenAttendancesIndicatorM1Data } from "@/services/esus/indicator/emulti/m1/getResumeCitizensAttendancesIndicatorM1";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEmultiM1AttendanceTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	healthTeam: Column;
	microarea: Column;
	totalAttendances: Column;
	totalGroupActivities: Column;
};

type IndicatorEmultiM1CitizensTableProps = {
	citizensWithAttendances: ResumeCitizenAttendancesIndicatorM1Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenId: number) => void;
};

export function IndicatorEmultiM1CitizensTable({
	citizensWithAttendances,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEmultiM1CitizensTableProps>) {
	const [columns, setColumns] = useState<IndicatorEmultiM1AttendanceTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		healthTeam: { title: "Equipe Vinculada", visible: false },
		microarea: { title: "MA", visible: false },
		totalAttendances: { title: "Atendimentos individuais", visible: true },
		totalGroupActivities: { title: "Atividades coletivas", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
						{columns.healthTeam.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th className={`px-2 ${!columns.totalAttendances.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalAttendances.title}
							<CardOverlayTrigger
								header="Atendimentos individuais"
								message="Atendimentos individuais do cidadão realizados por uma equipe emulti nos últimos 12 meses"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.totalGroupActivities.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalGroupActivities.title}
							<CardOverlayTrigger
								header="Atividades coletivas"
								message="Atividades coletivas do cidadão realizados por uma equipe emulti nos últimos 12 meses"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEmultiM1AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEmultiM1AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEmultiM1AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithAttendances.length > 0
					? citizensWithAttendances.map((citizenWithAttendances, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.fullName}</div>
										<div className="text-muted">
											{citizenWithAttendances.birthdate
												? dayjs(citizenWithAttendances.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.birthdate
										? getAge(citizenWithAttendances.birthdate)
										: ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.cpf
										? formatIdentifier(citizenWithAttendances.cpf)
										: citizenWithAttendances.cns ?? ""}
								</td>
								<td className={`px-2 ${!columns.healthTeam.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithAttendances.healthTeamName ?? ""}</div>
										<div className="text-muted">
											{citizenWithAttendances.healthTeamIne ?? ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.area ?? "FA"}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.totalAttendances.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.totalAttendances}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.totalGroupActivities.visible && "d-none"
									}`}
								>
									{citizenWithAttendances.totalGroupActivities}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() =>
											openCitizenHistory(citizenWithAttendances.id)
										}
										title="Histórico do cidadão"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
